import { type ComponentProps, useMemo } from 'react';
import {
  GridColumnsManagement,
  gridColumnDefinitionsSelector,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridRootProps,
  useGridSelector,
  type GridColumnVisibilityModel,
} from '@mui/x-data-grid-premium';
import { Box } from '@mui/material';

/** The set of fields a model explicitly hides (`value` is falsy). */
const hiddenFieldSet = (model: GridColumnVisibilityModel) =>
  new Set(Object.keys(model).filter((field) => !model[field]));

/** Two visibility models are equivalent when they hide the same fields. */
const modelsMatch = (
  a: GridColumnVisibilityModel,
  b: GridColumnVisibilityModel,
) => {
  const aHidden = hiddenFieldSet(a);
  const bHidden = hiddenFieldSet(b);
  return (
    aHidden.size === bHidden.size &&
    [...aHidden].every((field) => bHidden.has(field))
  );
};

type ManagedColumnsManagementProps = ComponentProps<
  typeof GridColumnsManagement
> & {
  /**
   * The visibility baseline restored when the user clicks Reset. Typically the
   * `defaultColumnVisibilityModel` from `useManagedColumnVisibility`, or a grid's
   * `initialState.columns.columnVisibilityModel` for uncontrolled grids.
   */
  defaultColumnVisibilityModel?: GridColumnVisibilityModel;
};

/**
 * Drop-in replacement for the Data Grid `columnsManagement` slot that fixes the
 * broken "Reset" button (EUD-612).
 *
 * MUI's built-in Reset restores to an internal "initial" model that is
 * re-pointed to the *current* selection whenever the grid's `columns` prop
 * reference changes (data load, grouping, mobile toggle, …). On controlled,
 * data-driven, or grouped grids that reference churns constantly, so the button
 * ends up disabled (current === initial) and "does nothing".
 *
 * This renders MUI's column list with its own footer disabled, then reproduces
 * that footer (same `baseCheckbox`/`baseButton` slots, locale text, and layout,
 * so it is visually identical to the native one) with a Reset that
 * deterministically restores `defaultColumnVisibilityModel` through the grid API
 * — independent of MUI's drift-prone baseline.
 */
export const ManagedColumnsManagement = ({
  defaultColumnVisibilityModel,
  getTogglableColumns,
  ...columnsManagementProps
}: ManagedColumnsManagementProps) => {
  const apiRef = useGridApiContext();
  const rootProps = useGridRootProps();
  const currentModel = useGridSelector(
    apiRef,
    gridColumnVisibilityModelSelector,
  );
  const columns = useGridSelector(apiRef, gridColumnDefinitionsSelector);

  // The columns the user can actually toggle (hideable + any consumer filter).
  const togglableColumns = useMemo(() => {
    const allowed = getTogglableColumns ? getTogglableColumns(columns) : null;
    return columns.filter(
      (col) =>
        col.hideable !== false &&
        (allowed == null || allowed.includes(col.field)),
    );
  }, [columns, getTogglableColumns]);

  const hidden = hiddenFieldSet(currentModel);
  const allVisible = togglableColumns.every((col) => !hidden.has(col.field));
  const allHidden = togglableColumns.every((col) => hidden.has(col.field));

  const isAtDefault = useMemo(
    () => modelsMatch(currentModel, defaultColumnVisibilityModel ?? {}),
    [currentModel, defaultColumnVisibilityModel],
  );

  const handleToggleAll = (isVisible: boolean) => {
    const next: GridColumnVisibilityModel = { ...currentModel };
    togglableColumns.forEach((col) => {
      next[col.field] = isVisible;
    });
    apiRef.current.setColumnVisibilityModel(next);
  };

  const handleReset = () => {
    apiRef.current.setColumnVisibilityModel({
      ...(defaultColumnVisibilityModel ?? {}),
    });
  };

  // Reuse the grid's own slots + locale text so the footer is indistinguishable
  // from MUI's native one on grids that keep the stock control.
  const BaseCheckbox = rootProps.slots.baseCheckbox;
  const BaseButton = rootProps.slots.baseButton;
  const showHideAllText = apiRef.current.getLocaleText(
    'columnsManagementShowHideAllText',
  );
  const resetText = apiRef.current.getLocaleText('columnsManagementReset');

  return (
    <Box>
      <GridColumnsManagement
        {...columnsManagementProps}
        getTogglableColumns={getTogglableColumns}
        disableShowHideToggle
        disableResetButton
      />
      <Box
        className='MuiDataGrid-columnsManagementFooter'
        sx={(theme) => ({
          display: 'flex',
          justifyContent: 'space-between',
          padding: theme.spacing(1, 1, 1, 1.5),
          // Match MUI's native footer divider token (lighter than theme.divider)
          // so the line is identical to the panel's other borders.
          borderTop: '1px solid var(--DataGrid-t-color-border-base)',
        })}
      >
        <BaseCheckbox
          density='compact'
          disabled={togglableColumns.length === 0}
          checked={allVisible}
          indeterminate={!allVisible && !allHidden}
          onChange={() => {
            handleToggleAll(!allVisible);
          }}
          name={showHideAllText}
          label={showHideAllText}
          {...rootProps.slotProps?.baseCheckbox}
        />
        <BaseButton
          onClick={handleReset}
          disabled={isAtDefault}
          {...rootProps.slotProps?.baseButton}
        >
          {resetText}
        </BaseButton>
      </Box>
    </Box>
  );
};

export default ManagedColumnsManagement;
